import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaRegTrashCan } from "react-icons/fa6";
import "react-datepicker/dist/react-datepicker.css";

import { IProduct } from "@/types/product.types";
import { IItem, IPurchaseRequisition } from "@/types/purchaseRequisition.type";
import { getProducts } from "@/common/services/product.api";
import { updatePurchaseRequisition } from "@/common/services/purchaseRequisition.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getCompanyId } from "@/common/services/company.api";

type Props = {
  requisition: IPurchaseRequisition;
  products?: IProduct[];
  closeModal?: () => void;
  onSaved?: (updated: IPurchaseRequisition) => void;
};

const PRIORITY_OPTIONS = ["HIGH", "MEDIUM", "LOW"];
const PRIORITY_LABELS: Record<string, string> = {
  HIGH: "Alta",
  MEDIUM: "Média",
  LOW: "Baixa",
};

type RowState = {
  query: string;
  open: boolean;
  highlighted: number;
};

export default function ModalEditPurchaseRequisition({
  requisition,
  products: productsProp,
  closeModal,
  onSaved,
}: Props) {
  const company = getCompanyId();
  const { notifySuccess, notifyError } = useToastContext();

  const initialItem: IItem = {
    productId: "",
    productName: "",
    quantity: "1",
    deliveryDate: null,
  };

  const [internalProducts, setInternalProducts] = useState<IProduct[]>([]);
  const products = productsProp ?? internalProducts;

  const [selectedItens, setSelectedItens] = useState<IItem[]>(
    (requisition?.itens?.length ? requisition.itens : [initialItem]).map((it) => ({
      productId: it.productId ?? "",
      productName: it.productName ?? "",
      quantity: String(it.quantity ?? "1"),
      deliveryDate: it.deliveryDate ?? null,
    }))
  );
  const [licensePlate, setLicensePlate] = useState(requisition.licensePlate ?? "");
  const [observation, setObservation] = useState(requisition.observation ?? "");
  const [priority, setPriority] = useState(requisition.priority ?? "Média");

  useEffect(() => {
    if (productsProp) return;
    const load = async () => {
      try {
        const result = await getProducts({
          page: 1,
          itemsPerPage: 10000,
          orderBy: "name",
        });
        if (result?.data) setInternalProducts(result.data);
      } catch (e) { 
        console.error(e)
      }
    };
    load();
  }, [productsProp, company]);

  const handleAddItemLine = () => {
    setSelectedItens((prev) => [...prev, initialItem]);
  };

  const removeItem = (index: number) => {
    setSelectedItens((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof IItem, value: IItem[keyof IItem]) => {
    const updated = [...selectedItens];
    (updated[index] as unknown as (string | Date))[field] = value;
    setSelectedItens(updated);
  };

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [rowsState, setRowsState] = useState<Record<number, RowState>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const initial: Record<number, RowState> = {};
    selectedItens.forEach((item, i) => {
      initial[i] = { query: item.productName || "", open: false, highlighted: -1 };
    });
    setRowsState((prev) => ({ ...initial, ...prev }));
  }, [selectedItens.length, selectedItens]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setRowsState((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => (next[Number(k)].open = false));
          return next;
        });
        setActiveIndex(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const makeFiltered = useCallback((q: string) =>
    q.trim() === "" ? products : products?.filter((p) => `${p.code} ${p.name}`.toLowerCase().includes(q.toLowerCase())), [products]);

  const filteredCache = useMemo(() => {
    const map = new Map<number, IProduct[]>();
    Object.entries(rowsState).forEach(([i, st]) => map.set(Number(i), makeFiltered(st.query) ?? []));
    return map;
  }, [rowsState, makeFiltered]);

  const onChangeQuery = (i: number, value: string) => {
    setRowsState((prev) => ({
      ...prev,
      [i]: { ...(prev[i] || { query: "", open: false, highlighted: -1 }), query: value, open: true, highlighted: -1 },
    }));
    updateItem(i, "productName", value);
    setActiveIndex(i);
  };

  const onSelectProduct = (i: number, product: IProduct) => {
    updateItem(i, "productId", product._id);
    updateItem(i, "productName", `${product.code} – ${product.name}`);
    setRowsState((prev) => ({
      ...prev,
      [i]: { ...(prev[i] || ({ query: "" } as RowState)), open: false, query: `${product.code} – ${product.name}`, highlighted: -1 },
    }));
    setActiveIndex(null);
  };

  const onQuantityChange = (i: number, raw: string) => {
    const cleaned = raw.replace(/[^\d]/g, "");
    const normalized = cleaned.replace(/^0+/, "");
    const finalVal = normalized === "" ? "" : String(Math.max(1, Number(normalized)));
    updateItem(i, "quantity", finalVal);
  };

  const onQuantityBlur = (i: number, value: unknown) => {
    const n = Number(value);
    const finalVal = Number.isFinite(n) && n >= 1 ? String(n) : "1";
    updateItem(i, "quantity", finalVal);
  };

  const onKeyDownCombo = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const list = filteredCache.get(i) || [];
    if (!rowsState[i]?.open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setRowsState((prev) => ({ ...prev, [i]: { ...(prev[i] as RowState), open: true } }));
      setActiveIndex(i);
      return;
    }
    if (!list.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setRowsState((prev) => {
        const h = Math.min(list.length - 1, (prev[i]?.highlighted ?? -1) + 1);
        return { ...prev, [i]: { ...(prev[i] as RowState), highlighted: h } };
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setRowsState((prev) => {
        const h = Math.max(-1, (prev[i]?.highlighted ?? -1) - 1);
        return { ...prev, [i]: { ...(prev[i] as RowState), highlighted: h } };
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const h = rowsState[i]?.highlighted ?? -1;
      const chosen = h >= 0 ? list[h] : list[0];
      if (chosen) onSelectProduct(i, chosen);
    } else if (e.key === "Escape") {
      setRowsState((prev) => ({ ...prev, [i]: { ...(prev[i] as RowState), open: false, highlighted: -1 } }));
      setActiveIndex(null);
    }
  };

  const handleSave = async () => {
    const payload: Partial<IPurchaseRequisition> = {
      itens: selectedItens,
      licensePlate,
      observation,
      priority,
    };
    try {
      const id = (requisition)?._id || (requisition)?.id;
      const result = await updatePurchaseRequisition(String(id), payload);
      if ((result)?.ok) {
        notifySuccess("Requisição atualizada com sucesso!");
        const updated = { ...requisition, ...payload } as IPurchaseRequisition;
        onSaved?.(updated);
        closeModal?.();
        return;
      }
      notifyError((result)?.message ?? "Falha ao atualizar.");
    } catch (e) {
      notifyError((e as Error)?.message ?? "Erro inesperado ao atualizar.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4">
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        className="bg-white z-10 w-full max-w-4xl rounded-2xl shadow-2xl p-8 relative overflow-y-auto max-h-[90vh] border border-gray-200"
      >
        <button
          onClick={closeModal}
          aria-label="Close modal"
          className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-2xl font-bold transition"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">
          Editar Requisição de Compra
        </h2>

        <div className="flex mb-4">
          <button
            onClick={handleAddItemLine}
            className="mt-2 w-56 font-semibold transition py-2 px-4 rounded-md bg-locavibe text-white hover:bg-red-700"
          >
            Adicionar item
          </button>
        </div>

        {selectedItens.map((item, index) => {
          const st = rowsState[index] || { query: item.productName || "", open: false, highlighted: -1 };
          const list = filteredCache.get(index) || [];
          const key = item?.id ?? index;

          return (
            <div
              key={key}
              className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6"
            >
              <div className="flex-1 relative w-full">
                <label
                  htmlFor={`product-${index}`}
                  className="text-sm font-semibold mb-1 block text-gray-700"
                >
                  Item *
                </label>
                <input
                  id={`product-${index}`}
                  type="text"
                  className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  placeholder="Digite para buscar..."
                  value={st.query}
                  onChange={(e) => onChangeQuery(index, e.target.value)}
                  onFocus={() => {
                    setRowsState((prev) => ({
                      ...prev,
                      [index]: { ...(prev[index] || ({ query: "" } as RowState)), open: true },
                    }));
                    setActiveIndex(index);
                  }}
                  onKeyDown={(e) => onKeyDownCombo(index, e)}
                  role="combobox"
                  aria-expanded={st.open}
                  aria-controls={`listbox-${index}`}
                  aria-autocomplete="list"
                />

                {st.open && list.length > 0 && activeIndex === index && (
                  <ul
                    id={`listbox-${index}`}
                    role="listbox"
                    className="absolute left-0 right-0 bg-white mt-2 max-h-60 overflow-auto border shadow-lg z-50"
                  >
                    {list.map((product, i) => {
                      const isActive = i === st.highlighted;
                      return (
                        <li
                          key={product._id}
                          role="option"
                          aria-selected={isActive}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onSelectProduct(index, product)}
                          className={`cursor-pointer px-4 py-2 text-sm transition ${isActive ? "bg-blue-600 text-white" : "hover:bg-blue-500 hover:text-white"
                            }`}
                        >
                          {product.code} – {product.name}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="w-full md:w-32">
                <label
                  htmlFor={`qty-${index}`}
                  className="text-sm font-semibold text-gray-700 block mb-1"
                >
                  Quantidade *
                </label>
                <input
                  id={`qty-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  value={item.quantity ?? ""}
                  onChange={(e) => onQuantityChange(index, e.target.value)}
                  onBlur={() => onQuantityBlur(index, item.quantity)}
                />
              </div>

              <div className="flex items-end">
                <button
                  aria-label="Remover item"
                  className="text-red-500 text-2xl hover:text-red-600 transition"
                  onClick={() => removeItem(index)}
                >
                  <FaRegTrashCan />
                </button>
              </div>
            </div>
          );
        })}

        <hr className="mb-6" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label htmlFor="licensePlate" className="text-sm font-semibold mb-1 block text-gray-700">
              Placa
            </label>
            <input
              id="licensePlate"
              type="text"
              className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={licensePlate}
              onChange={(e) => setLicensePlate(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="priority" className="text-sm font-semibold mb-1 block text-gray-700">
              Prioridade
            </label>
            <select
              id="priority"
              className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {PRIORITY_LABELS[opt] ?? opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-8">
          <label htmlFor="observation" className="text-sm font-semibold mb-1 block text-gray-700">
            Observações
          </label>
          <textarea
            id="observation"
            rows={3}
            className="w-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="Adicione observações se necessário..."
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={(selectedItens?.length ?? 0) < 1}
            className={`mt-2 w-56 font-semibold transition py-2 px-4 rounded-md ${(selectedItens?.length ?? 0) < 1 ? "bg-gray5 text-white cursor-not-allowed" : "bg-locavibe text-white hover:bg-red-700"
              }`}
          >
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  );
}
